import { ArrayUtil } from "@coten-inc/utils";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { RotateCw } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import { EmptyState } from "~/components/empty-state";
import { Button } from "~/components/ui";
import { useGeoItemList } from "~/features/geoItems/hooks";
import { geoItemDisplayName, geoItemPlaceName } from "~/features/recordGeoItems/utils/geoItemLabel";
import { formatJdnAsGdtfDate } from "~/utils/gdtfRange";
import { v2Route } from "~/v2/routes";

interface GeoItemsTableProps {
  label?: string;
  pageIndex: number;
  pageSize: number;
  isPending: boolean;
  onPageIndexChange: (next: number) => void;
  onPageSizeChange: (next: number) => void;
}

interface GeoItemRow {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  relatedEntities: string[];
}

const helper = createColumnHelper<DataTableFeatures, GeoItemRow>();
const sorting: SortingState = [];

export function GeoItemsTable({
  label,
  pageIndex,
  pageSize,
  isPending,
  onPageIndexChange,
  onPageSizeChange,
}: GeoItemsTableProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const navigate = useNavigate();
  const { data, refetch } = useGeoItemList({ label, pageIndex, pageSize });
  const rows = useMemo<GeoItemRow[]>(
    () =>
      data.cnutSearchGeoItems.items.map((item) => ({
        id: item.id,
        label: geoItemDisplayName(item.id, geoItemPlaceName(item.i18nLabel, locale)),
        startDate: formatJdnAsGdtfDate(item.fromJdn),
        endDate: formatJdnAsGdtfDate(item.toJdn),
        // One entry per link, so a Record linking this GeoItem several times is listed once.
        relatedEntities: ArrayUtil.uniqBy(
          item.recordGeoItems.map(({ record }) => record),
          "id",
        )
          .map(({ name }) => name)
          .filter((name): name is string => !!name),
      })),
    [data.cnutSearchGeoItems.items, locale],
  );
  const columns = [
    helper.accessor("label", {
      header: () => t("model.geoItem.i18nLabel"),
      enableSorting: false,
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue() || "—"}</span>,
    }),
    helper.accessor("id", {
      header: () => t("ui.id"),
      enableSorting: false,
      cell: (info) => <span className="font-mono text-[12px] text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("startDate", {
      header: () => t("model.geoItem.startDate"),
      enableSorting: false,
    }),
    helper.accessor("endDate", {
      header: () => t("model.geoItem.endDate"),
      enableSorting: false,
    }),
    helper.accessor("relatedEntities", {
      header: () => t("model.geoItem.relatedEntities"),
      enableSorting: false,
      cell: (info) => {
        const names = info.getValue();
        return names.length > 0 ? names.join(", ") : <span className="text-fg-3">—</span>;
      },
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      cell: (info) => (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(v2Route.geoItems.detail.$buildPath({ params: { id: info.row.original.id } }))}
          >
            {t("ui.detail")}
          </Button>
        </div>
      ),
    }),
  ];

  if (rows.length === 0 && !isPending) {
    return (
      <EmptyState title={t("feature.geoItems.empty.title")} description={t("feature.geoItems.empty.description")} />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="icon" onClick={() => void refetch()} aria-label={t("ui.reload")}>
          <RotateCw />
        </Button>
      </div>
      <RemoteDataTable<GeoItemRow>
        columns={columns}
        data={rows}
        rowCount={data.cnutSearchGeoItems.total}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        sorting={sorting}
        onSortingChange={() => undefined}
        isPending={isPending}
        onRowClick={(row) => navigate(v2Route.geoItems.detail.$buildPath({ params: { id: row.id } }))}
        emptyMessage={t("feature.common.noData")}
      />
    </div>
  );
}
